import React, { useState } from 'react';
import { Tabs } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import RootLayout from '@/components/layouts/RootLayout';
import { ContainerFluid, Section } from '@/components/layouts';
import { ContentBackground } from '@/components/content';
import { ContentWrapper, ContentHeader, ContentBody } from '@/components/content';
import { ROLES_PERMISSIONS } from '@/constants';
import { QueryParamsProvider } from '@/contexts/query-params/QueryParamsProvider';
import { API } from '@/services/api.service';
import { FINANCIAL_STATEMENTS_DEFAULT_PARAMS } from '@/shared/financialStatements';
import StatementsList from './_components/table/StatementsList';
import UploadPanel from './_components/UploadPanel';
import MappingsPanel from './_components/MappingsPanel';
import ValidationRulesPanel from './_components/ValidationRulesPanel';
import PeriodicReportPanel from './_components/PeriodicReportPanel';

const QUERY_KEYS = {
  COMPANY_SIZES: 'financial-statements-company-sizes',
  STATUSES: 'financial-statements-statuses',
  COMPANIES_DROPDOWN: 'financial-statements-companies-dropdown',
  MAPPINGS: 'financial-statements-mappings',
  VALIDATION_RULES: 'financial-statements-validation-rules',
  VALIDATION_SEVERITIES: 'financial-statements-validation-severities',
  VALIDATION_OPERATORS: 'financial-statements-validation-operators',
};

function FinancialStatementsContent() {
  const { t, i18n } = useTranslation(['tables', 'menus', 'forms', 'messages']);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('statements');

  const fs = (key) => t(`financialStatements.${key}`, { ns: 'tables' });

  // Fetch companies dropdown
  const { data: companiesData } = useQuery({
    queryKey: [QUERY_KEYS.COMPANIES_DROPDOWN],
    queryFn: () => API.financialStatements.getCompaniesDropdown(),
  });

  const companies = companiesData?.data || [];

  // Fetch company sizes enum
  const { data: companySizesData } = useQuery({
    queryKey: [QUERY_KEYS.COMPANY_SIZES],
    queryFn: () => API.financialStatements.getCompanySizesEnum(),
  });

  const companySizes = companySizesData?.data || [];

  // Fetch mappings
  const { data: mappingsData, isLoading: mappingsLoading } = useQuery({
    queryKey: [QUERY_KEYS.MAPPINGS],
    queryFn: () => API.financialStatements.getAopMappings({ limit: 100 }),
    enabled: activeTab === 'mappings',
  });

  const mappings = mappingsData?.data || [];

  // Fetch validation rules
  const { data: validationRulesData, isLoading: validationRulesLoading } = useQuery({
    queryKey: [QUERY_KEYS.VALIDATION_RULES],
    queryFn: () => API.financialStatements.getValidationRules({ limit: 100 }),
    enabled: activeTab === 'validations',
  });

  const validationRules = validationRulesData?.data || [];

  // Fetch validation severities enum
  const { data: validationSeveritiesData } = useQuery({
    queryKey: [QUERY_KEYS.VALIDATION_SEVERITIES],
    queryFn: () => API.financialStatements.getValidationSeveritiesEnum(),
    enabled: activeTab === 'validations',
  });

  const validationSeverities = validationSeveritiesData?.data || [];

  // Fetch validation operators enum
  const { data: validationOperatorsData } = useQuery({
    queryKey: [QUERY_KEYS.VALIDATION_OPERATORS],
    queryFn: () => API.financialStatements.getValidationOperatorsEnum(),
    enabled: activeTab === 'validations',
  });

  const validationOperators = validationOperatorsData?.data || [];

  const handleUploadSuccess = () => {
    queryClient.invalidateQueries(['financial-statements']);
    setActiveTab('statements');
  };

  const getLocalizedLabel = (item) => {
    const lang = i18n.language;
    if (lang === 'sr') return item.labelSr || item.label;
    if (lang === 'ru') return item.labelRu || item.label;
    return item.labelEn || item.label;
  };

  const tabItems = [
    {
      key: 'statements',
      label: fs('statementsList'),
      children: <StatementsList />,
    },
    {
      key: 'upload',
      label: fs('uploadNew'),
      children: (
        <UploadPanel
          companies={companies}
          companySizes={companySizes}
          onSuccess={handleUploadSuccess}
          getLocalizedLabel={getLocalizedLabel}
        />
      ),
    },
    {
      key: 'periodic',
      label: fs('periodicTab'),
      children: (
        <PeriodicReportPanel
          companySizes={companySizes}
          getLocalizedLabel={getLocalizedLabel}
        />
      ),
    },
    {
      key: 'mappings',
      label: fs('aopMappings'),
      children: (
        <MappingsPanel
          mappings={mappings}
          loading={mappingsLoading}
          companySizes={companySizes}
          getLocalizedLabel={getLocalizedLabel}
        />
      ),
    },
    {
      key: 'validations',
      label: fs('validationRules'),
      children: (
        <ValidationRulesPanel
          validationRules={validationRules}
          loading={validationRulesLoading}
          companySizes={companySizes}
          validationSeverities={validationSeverities}
          validationOperators={validationOperators}
          getLocalizedLabel={getLocalizedLabel}
        />
      ),
    },
  ];

  return (
    <RootLayout metadata={{ title: t('financialStatements', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />

      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('financialStatements', { ns: 'menus' })} />

            <ContentBody>
              <div className="with-y-overflow">
                <div className="p-4">
                  <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                  />
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

function FinancialStatementsPage() {
  return (
    <QueryParamsProvider defaultParams={FINANCIAL_STATEMENTS_DEFAULT_PARAMS}>
      <FinancialStatementsContent />
    </QueryParamsProvider>
  );
}

const FinancialStatementsPageWithAuth = withPrivateRoute(FinancialStatementsPage, ROLES_PERMISSIONS.FINANCIAL_STATEMENTS.VIEW);
FinancialStatementsPageWithAuth.displayName = 'FinancialStatementsPage';

export default FinancialStatementsPageWithAuth;
